import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [currentOrg, setCurrentOrg] = useState(null); // { id, name, role }

  // Separate async operations object
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        // Try to fetch user profile
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle(); // Use maybeSingle() instead of single() to avoid 406 errors

        if (error) {
          console.error('Error loading user profile:', error);

          // If profile doesn't exist, try to create it
          if (error.code === 'PGRST116' || !data) {
            console.log('Profile not found, creating one...');
            const { data: { user } } = await supabase.auth.getUser();

            if (user) {
              const { data: newProfile, error: createError } = await supabase
                .from('user_profiles')
                .insert({
                  id: userId,
                  email: user.email,
                  full_name: user.user_metadata?.full_name || user.email?.split('@')[0],
                  role: 'user'
                })
                .select()
                .single();

              if (!createError && newProfile) {
                setUserProfile(newProfile);
              } else {
                console.error('Error creating profile:', createError);
              }
            }
          }
        } else if (data) {
          setUserProfile(data);
        }

        // Load organization membership (no org switcher; pick first if any).
        try {
          const { data: memberships, error: memErr } = await supabase
            .from('organization_members')
            .select('org_id, role, organizations(name)')
            .eq('user_id', userId);
          if (memErr) throw memErr;
          if (memberships && memberships.length > 0) {
            const m = memberships[0];
            setCurrentOrg({ id: m.org_id, name: m.organizations?.name || 'Organization', role: m.role });
          } else {
            // Fallback for superadmin: pick the most recent organization so app pages can operate
            const isSA = (supabase.auth.getUser && (await supabase.auth.getUser())?.data?.user?.user_metadata?.role === 'superadmin')
              || (typeof window !== 'undefined' && window?.localStorage?.getItem('isSA') === 'true');
            if (isSA) {
              try {
                const { data: orgs, error: orgErr } = await supabase
                  .from('organizations')
                  .select('id, name')
                  .order('created_at', { ascending: false })
                  .limit(1);
                if (!orgErr && orgs && orgs.length > 0) {
                  setCurrentOrg({ id: orgs[0].id, name: orgs[0].name, role: 'owner' });
                } else {
                  setCurrentOrg(null);
                }
              } catch {
                setCurrentOrg(null);
              }
            } else {
              setCurrentOrg(null);
            }
          }
        } catch (e) {
          console.error('Error loading org membership:', e);
          setCurrentOrg(null);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setProfileLoading(false);
      }
    },
    
    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id); // Fire-and-forget
      } else {
        profileOperations?.clear();
        setCurrentOrg(null);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    // Safety: never hang the app indefinitely on startup
    const safetyTimer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 4000);

    // Get initial session (robust to errors)
    supabase?.auth?.getSession?.()
      ?.then(({ data: { session }, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('auth.getSession error:', error);
          setLoading(false);
          return;
        }
        authStateHandlers?.onChange(null, session);
      })
      ?.catch((e) => {
        if (cancelled) return;
        console.error('auth.getSession failed:', e);
        setLoading(false);
      });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange?.(
      authStateHandlers?.onChange
    ) || { data: { subscription: null } };

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
      subscription?.unsubscribe?.();
    };
  }, []);

  // Authentication functions
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        if (error?.message?.includes('Failed to fetch') || 
            error?.message?.includes('AuthRetryableFetchError')) {
          throw new Error('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.');
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
        }
      });
      
      if (error) {
        if (error?.message?.includes('Failed to fetch') || 
            error?.message?.includes('AuthRetryableFetchError')) {
          throw new Error('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.');
        }
        throw error;
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    currentOrg,
    currentOrgId: currentOrg?.id || null,
    orgRole: currentOrg?.role || null,
    signIn,
    signUp,
    signOut,
    resetPassword,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'admin',
    isSuperadmin: (user?.user_metadata?.role === 'superadmin')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
